import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';

function AdminPlatformConfig() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlatform, setNewPlatform] = useState({
    platformName: '',
    commissionRate: 0,
    notes: ''
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const API_BASE = `${API_URL}/admin/platform-config`;

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_BASE);
      const data = await response.json();
      setPlatforms(data.platforms || []);
    } catch (err) {
      showMessage('Failed to load platforms', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleEdit = (platform) => {
    setEditingId(platform.id);
    setEditForm({
      commissionRate: platform.commissionRate,
      notes: platform.notes
    });
  };

  const handleSave = async (platformId) => {
    try {
      const response = await fetch(`${API_BASE}/${platformId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Update failed');

      showMessage('Platform updated!', 'success');
      setEditingId(null);
      fetchPlatforms();
    } catch (err) {
      showMessage('Update failed', 'error');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPlatform)
      });

      if (!response.ok) throw new Error('Add failed');

      showMessage('Platform added!', 'success');
      setShowAddForm(false);
      setNewPlatform({ platformName: '', commissionRate: 0, notes: '' });
      fetchPlatforms();
    } catch (err) {
      showMessage('Add failed', 'error');
    }
  };

  const handleToggle = async (platformId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/${platformId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!response.ok) throw new Error('Toggle failed');

      showMessage('Status updated!', 'success');
      fetchPlatforms();
    } catch (err) {
      showMessage('Toggle failed', 'error');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setNewPlatform({ platformName: '', commissionRate: 0, notes: '' });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading platforms...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="admin-btn admin-btn-primary"
        >
          {showAddForm ? 'Cancel' : '+ Add Platform'}
        </button>
      </div>

      {message && (
        <div className={`admin-alert ${messageType}`}>
          {message}
        </div>
      )}

      {/* Add Platform Form */}
      {showAddForm && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-header">Add New Platform</div>
          <div className="admin-card-body">
            <div className="admin-form-row">
              <div className="admin-field">
                <label className="admin-label">Platform Name *</label>
                <input
                  type="text"
                  value={newPlatform.platformName}
                  onChange={(e) => setNewPlatform({ ...newPlatform, platformName: e.target.value })}
                  className="admin-input"
                  placeholder="e.g., Playo, Huddle"
                  required
                />
              </div>
              <div className="admin-field">
                <label className="admin-label">Commission Rate (%) *</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={newPlatform.commissionRate}
                  onChange={(e) => setNewPlatform({ ...newPlatform, commissionRate: parseFloat(e.target.value) || 0 })}
                  className="admin-input"
                  placeholder="e.g., 18.00"
                  required
                />
              </div>
              <div className="admin-field">
                <label className="admin-label">Notes</label>
                <input
                  type="text"
                  value={newPlatform.notes}
                  onChange={(e) => setNewPlatform({ ...newPlatform, notes: e.target.value })}
                  className="admin-input"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={cancelAdd} className="admin-btn admin-btn-secondary">Cancel</button>
              <button
                onClick={handleAdd}
                disabled={!newPlatform.platformName || newPlatform.commissionRate < 0}
                className="admin-btn admin-btn-primary"
              >
                Add Platform
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Platforms Table */}
      <div className="admin-card">
        <div className="admin-card-header">Platforms ({platforms.length})</div>

        {platforms.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">🔧</div>
            <p>No platforms configured</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Commission Rate</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {platforms.map((platform) => (
                  <tr key={platform.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '14px'
                        }}>
                          {platform.platformName.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{platform.platformName}</span>
                      </div>
                    </td>
                    <td>
                      {editingId === platform.id ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={editForm.commissionRate}
                          onChange={(e) => setEditForm({ ...editForm, commissionRate: parseFloat(e.target.value) || 0 })}
                          className="admin-input"
                          style={{ width: '100px' }}
                        />
                      ) : (
                        <span style={{ fontWeight: 600, color: '#667eea', fontSize: '16px' }}>{platform.commissionRate}%</span>
                      )}
                    </td>
                    <td>
                      <span className={`admin-badge ${platform.isActive ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                        {platform.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      {editingId === platform.id ? (
                        <input
                          type="text"
                          value={editForm.notes || ''}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          className="admin-input"
                          placeholder="Add notes..."
                        />
                      ) : (
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>{platform.notes || '—'}</span>
                      )}
                    </td>
                    <td>
                      {editingId === platform.id ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => handleSave(platform.id)} className="admin-btn admin-btn-primary admin-btn-sm">Save</button>
                          <button onClick={cancelEdit} className="admin-btn admin-btn-secondary admin-btn-sm">Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => handleEdit(platform)} className="admin-btn admin-btn-primary admin-btn-sm">Edit</button>
                          <button
                            onClick={() => handleToggle(platform.id, platform.isActive)}
                            className={`admin-btn admin-btn-sm ${platform.isActive ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                          >
                            {platform.isActive ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPlatformConfig;
